import React, { useState } from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { theme } from '../config/theme';
import { locations } from '../config/locations';
import { Button } from '../junto-frontend/components/ui';
import { getComponent } from '../config/imageRegistry';

const FooterContainer = styled.footer`
  background-color: ${theme.colors.primary.background};
  padding: ${theme.spacing.xl} ${theme.spacing.layoutGutter} ${theme.spacing.sm};
  border-top: 1px solid ${theme.shadows.medium};
`;

const FooterContent = styled.div`
  max-width: 1440px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: center;
  gap: ${theme.spacing.xl};

  @media (max-width: ${theme.breakpoints.tablet}) {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: ${theme.spacing.xl};
    text-align: center;
  }
`;

const Logo = styled(Link)`
  display: flex;
  align-items: center;
  margin-left: 40px;
  text-decoration: none;
  transition: ${theme.transitions.default};
  cursor: pointer;
  
  &:hover {
    svg {
      *, g, path {
        fill: ${theme.colors.accent.main} !important;
      }
    }
  }

  @media (max-width: ${theme.breakpoints.tablet}) {
    margin-left: 0;
    order: -1;
    margin-bottom: ${theme.spacing.lg};
  }
`;

const LogoSVG = getComponent('site_logo');

const StyledLogoSVG = styled(LogoSVG)`
  height: 64px;
  width: auto;
  
  *, g, path {
    fill: ${theme.colors.primary.ui_dark} !important;
    transition: ${theme.transitions.default};
  }
`;

const ContactForm = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.md};
  max-width: 300px;
  width: 100%;
  
  @media (max-width: ${theme.breakpoints.tablet}) {
    max-width: 100%;
    order: 0;
  }
`;

const ContactTitle = styled.div`
  font-family: ${theme.typography.button.fontFamily};
  font-size: ${theme.typography.button.fontSize};
  font-weight: ${theme.typography.button.fontWeight};
  letter-spacing: ${theme.typography.button.letterSpacing};
  text-transform: ${theme.typography.button.textTransform};
  color: ${theme.colors.primary.text};
  margin-bottom: ${theme.spacing.sm};
  text-align: center;
`;

const FormField = styled.div`
  display: flex;
  flex-direction: column;
`;

const Input = styled.input`
  background: transparent;
  border: none;
  border-bottom: 1px solid ${theme.colors.primary.ui_dark};
  font-family: ${theme.typography.secondary.fontFamily};
  font-size: 0.85rem;
  color: ${theme.colors.primary.text};
  padding: ${theme.spacing.sm} 0;
  opacity: 0.7;
  transition: ${theme.transitions.default};
  
  &::placeholder {
    color: ${theme.colors.primary.text};
    opacity: 0.5;
    font-style: italic;
  }
  
  &:focus {
    outline: none;
    border-bottom-color: ${theme.colors.accent.main};
    opacity: 1;
  }
  
  &:hover {
    transform: scale(1.02);
  }
  
  &.required::placeholder::after {
    content: '*';
    color: ${theme.colors.accent.main};
  }
  
  &.error {
    border-bottom-color: ${theme.colors.accent.deep};
  }
`;

const TextArea = styled.textarea`
  background: transparent;
  border: none;
  border-bottom: 1px solid ${theme.colors.primary.ui_dark};
  font-family: ${theme.typography.secondary.fontFamily};
  font-size: 0.85rem;
  color: ${theme.colors.primary.text};
  padding: ${theme.spacing.sm} 0;
  opacity: 0.7;
  transition: ${theme.transitions.default};
  resize: none;
  min-height: 60px;
  
  &::placeholder {
    color: ${theme.colors.primary.text};
    opacity: 0.5;
    font-style: italic;
  }
  
  &:focus {
    outline: none;
    border-bottom-color: ${theme.colors.accent.main};
    opacity: 1;
  }
  
  &:hover {
    transform: scale(1.02);
  }
`;

const SubmitButtonWrapper = styled.div`
  margin-top: ${theme.spacing.lg};
  align-self: center;
`;

const StatusMessage = styled.div`
  font-family: ${theme.typography.secondary.fontFamily};
  font-size: 0.75rem;
  text-align: center;
  margin-top: ${theme.spacing.sm};
  color: ${props => props.$error ? theme.colors.accent.deep : theme.colors.accent.main};
  opacity: ${props => props.$show ? 1 : 0};
  transition: opacity 0.3s ease;
`;

const FooterBottom = styled.div`
  max-width: 1440px;
  margin: 0 auto;
  margin-top: ${theme.spacing.lg};
  padding-top: ${theme.spacing.sm};
  border-top: 1px solid ${theme.shadows.light};
  display: flex;
  justify-content: space-between;
  align-items: center;
  
  @media (max-width: ${theme.breakpoints.tablet}) {
    flex-direction: column;
    gap: ${theme.spacing.sm};
    text-align: center;
  }
`;

const Copyright = styled.div`
  font-family: ${theme.typography.secondary.fontFamily};
  font-size: 0.75rem;
  color: ${theme.colors.primary.text};
  opacity: 0.7;
`;

const PrivacyTerms = styled.div`
  font-family: ${theme.typography.secondary.fontFamily};
  font-size: 0.75rem;
  color: ${theme.colors.primary.text};
  
  a {
    color: ${theme.colors.primary.text};
    text-decoration: underline;
    opacity: 0.7;
    transition: ${theme.transitions.default};
    
    &:hover {
      opacity: 1;
      color: ${theme.colors.accent.main};
    }
  }
`;

const ContactInfo = styled.div`
  text-align: right;
  margin-right: 40px;

  @media (max-width: ${theme.breakpoints.tablet}) {
    text-align: center;
    margin-right: 0;
    order: 1;
  }
`;

const ContactItem = styled.div`
  font-family: ${theme.typography.secondary.fontFamily};
  font-size: ${theme.typography.secondary.navigation.fontSize};
  color: ${theme.colors.primary.text};
  margin-bottom: ${theme.spacing.sm};

  &:last-child {
    margin-bottom: 0;
  }
`;

const ContactLink = styled.a`
  font-family: ${theme.typography.secondary.fontFamily};
  font-size: ${theme.typography.secondary.navigation.fontSize};
  color: ${theme.colors.primary.text};
  text-decoration: underline;
  transition: ${theme.transitions.default};

  &:hover {
    color: ${theme.colors.accent.main};
  }
`;


const InstagramIcon = styled.a`
  display: inline-block;
  margin-top: ${theme.spacing.sm};
  font-size: 1.5rem;
  color: ${theme.colors.primary.ui_dark};
  text-decoration: none;
  transition: ${theme.transitions.default};

  &:hover {
    color: ${theme.colors.accent.main};
    font-weight: bold;
  }

  i {
    font-size: 24px;
  }
`;

const Footer = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [status, setStatus] = useState({ show: false, message: '', error: false });
  const primaryLocation = locations[0];

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.email) {
      setStatus({
        show: true,
        message: 'Email is required',
        error: true
      });
      setTimeout(() => setStatus(prev => ({ ...prev, show: false })), 3000);
      return;
    }

    if (!validateEmail(formData.email)) {
      setStatus({
        show: true,
        message: 'Please enter a valid email address',
        error: true
      });
      setTimeout(() => setStatus(prev => ({ ...prev, show: false })), 3000);
      return;
    }

    if (!formData.message) {
      setStatus({
        show: true,
        message: 'Message is required',
        error: true
      });
      setTimeout(() => setStatus(prev => ({ ...prev, show: false })), 3000);
      return;
    }

    setIsSubmitting(true);

    try {
      // FormSubmit.co - no signup required!
      const formData2 = new FormData();
      formData2.append('name', formData.name || 'Anonymous');
      formData2.append('email', formData.email);
      formData2.append('message', formData.message);
      formData2.append('_replyto', formData.email);
      formData2.append('_subject', `New contact form submission from ${formData.name || 'Anonymous'}`);
      formData2.append('_captcha', 'false');

      const response = await fetch(`https://formsubmit.co/${primaryLocation.contact.email}`, {
        method: 'POST',
        body: formData2
      });

      if (response.ok) {
        setStatus({
          show: true,
          message: 'Message sent successfully!',
          error: false
        });

        // Reset form
        setFormData({ name: '', email: '', message: '' });
      } else {
        throw new Error('Form submission failed');
      }
      
    } catch (error) {
      console.error('Form submission error:', error);
      setStatus({
        show: true,
        message: 'Failed to send message. Please try again.',
        error: true
      });
    }

    setIsSubmitting(false);
    setTimeout(() => setStatus(prev => ({ ...prev, show: false })), 3000);
  };

  return (
    <FooterContainer>
      <FooterContent>
        <Logo 
          to="/" 
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
        >
          <StyledLogoSVG />
        </Logo>
        
        <ContactForm>
          <ContactTitle>Need help? Contact us</ContactTitle>
          <FormField>
            <Input 
              type="text" 
              placeholder="Name (optional)"
              value={formData.name}
              onChange={(e) => setFormData({...formData, name: e.target.value})}
            />
          </FormField>
          <FormField>
            <Input 
              type="email" 
              placeholder="Email *"
              value={formData.email}
              onChange={(e) => setFormData({...formData, email: e.target.value})}
              required
            />
          </FormField>
          <FormField>
            <TextArea 
              placeholder="Message *"
              value={formData.message}
              onChange={(e) => setFormData({...formData, message: e.target.value})}
              required
            />
          </FormField>
          <SubmitButtonWrapper>
            <Button 
              type="submit" 
              onClick={handleSubmit}
              disabled={isSubmitting}
              size="medium"
            >
              {isSubmitting ? 'Sending...' : 'Send'}
            </Button>
          </SubmitButtonWrapper>
          <StatusMessage $show={status.show} $error={status.error}>
            {status.message}
          </StatusMessage>
        </ContactForm>

        <ContactInfo>
          <ContactItem>
            <ContactLink href={`mailto:${primaryLocation.contact.email}`}>
              {primaryLocation.contact.email}
            </ContactLink>
          </ContactItem>
          <ContactItem>
            <ContactLink href={`tel:${primaryLocation.contact.phone.replace(/\D/g, '')}`}>
              {primaryLocation.contact.phone}
            </ContactLink>
          </ContactItem>
          {locations.map((location) => (
            <ContactItem key={location.id}>
              {location.displayName}: <ContactLink 
                href={location.mapUrl} 
                target="_blank" 
                rel="noopener noreferrer"
              >
                {location.address.street}
              </ContactLink>
            </ContactItem>
          ))}
          <InstagramIcon 
            href="https://www.instagram.com/the.sol.room/" 
            target="_blank" 
            rel="noopener noreferrer"
          >
            <i className="fa fa-instagram"></i>
          </InstagramIcon>
        </ContactInfo>
      </FooterContent>
      
      <FooterBottom>
        <Copyright>
          2025 Sol Movement LLC. All Rights Reserved.
        </Copyright>
        <PrivacyTerms>
          <Link 
            to="/privacy"
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          >
            Privacy
          </Link> / <Link 
            to="/terms"
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          >
            Terms
          </Link>
        </PrivacyTerms>
      </FooterBottom>
    </FooterContainer>
  );
};

export default Footer;